import { defineMessages } from 'react-intl';

export const scope = 'app.containers.ApprovalRules.ApprovalsQueue';

/**
 * Copy for the approvals queue and the offer review panel (AXHE-3947).
 *
 * recruiter-ui uses the legacy FormatJS extraction: manual dot-notation ids, and
 * `description` as the in-code record of what a string means. Both are required —
 * an omitted id silently never reaches the translation file.
 *
 * "Level" is the domain noun here and is deliberately kept: an approval level is
 * a round of sign-off inside a rule, not a workflow stage, and the rule editor
 * already labels them "Level 1 · Finance review". The level labels themselves are
 * imported from that catalog rather than restated.
 */
export default defineMessages({
  approvalsTab: {
    id: `${scope}.approvalsTab`,
    defaultMessage: 'Approvals',
    description:
      'Tab holding the offers currently waiting on the signed-in user for sign-off',
  },

  // --- the queue's columns (Design Requirements §5) ---

  // "Opening" for both entity types: an offer row names the opening it is on with
  // the applicant beneath it, and an opening row names itself. The Type column
  // beside it is the rule table's own header, reused.
  columnOpening: {
    id: `${scope}.columnOpening`,
    defaultMessage: 'Opening',
    description:
      'Column naming the job opening a row is about — the offer\'s opening on an offer row, the opening itself on an opening row',
  },
  columnApproverRule: {
    id: `${scope}.columnApproverRule`,
    defaultMessage: 'Approver rule',
    description:
      'Column naming which approval rules require sign-off on this row. Several can apply at once',
  },
  columnApprovalStatus: {
    id: `${scope}.columnApprovalStatus`,
    defaultMessage: 'Approval status',
    description:
      'Column giving how far the approval has got, one line per level of sign-off',
  },
  noLevels: {
    id: `${scope}.noLevels`,
    defaultMessage: 'No levels',
    description:
      'Shown in the Approval status column for an approval that has no levels of sign-off on it at all',
  },
  levelNeedsAttention: {
    id: `${scope}.levelNeedsAttention`,
    defaultMessage: 'Needs attention',
    description:
      'Marks a level of sign-off that is open but can never be cleared, because nobody was resolved onto it — a misconfiguration somebody has to fix',
  },
  columnActions: {
    id: `${scope}.columnActions`,
    defaultMessage: 'Actions',
    description:
      'Accessible name of the last column, holding the control that opens the review panel. No visible heading',
  },
  reviewOffer: {
    id: `${scope}.reviewOffer`,
    defaultMessage: 'Review',
    description:
      'Control on a queue row that opens the offer review panel for that row',
  },
  reviewOfferFor: {
    id: `${scope}.reviewOfferFor`,
    defaultMessage: 'Review the offer for {applicant}',
    description:
      "Accessible name of the Review control, naming which row it opens so the name is unique in a list. {applicant} is the applicant's name",
  },
  // The offer's own document, and the one thing on the row that names it. Verb +
  // noun, and "offer letter" rather than "offer": the row already says which
  // offer, and what this opens is the letter. On offer rows only — an opening has
  // no letter behind it.
  previewOfferLetter: {
    id: `${scope}.previewOfferLetter`,
    defaultMessage: 'Preview offer letter',
    description:
      'Link under the applicant name on an offer row, opening the review panel at the rendered letter',
  },
  // The visible label leads the accessible name rather than being reworded into
  // it — WCAG 2.5.3. "Preview *the* offer letter for…" would break the match a
  // speech-input user needs, because the words they can see would no longer be a
  // contiguous run of the name they can address.
  previewOfferLetterFor: {
    id: `${scope}.previewOfferLetterFor`,
    defaultMessage: 'Preview offer letter for {applicant}',
    description:
      "Accessible name of the Preview offer letter link, naming which row it opens so the name is unique in a list. Opens with the visible label verbatim. {applicant} is the applicant's name",
  },
  // --- deciding an opening from its row (AXHE-3934) ---

  // The buttons themselves are the shared "Approve" and "Reject"; only the
  // accessible names are local, because they have to name the row.
  approveOpeningFor: {
    id: `${scope}.approveOpeningFor`,
    defaultMessage: 'Approve the opening {opening}',
    description:
      'Accessible name of the Approve control on a queue row, naming which opening it signs off so the name is unique in a list. {opening} is the opening title',
  },
  rejectOpeningFor: {
    id: `${scope}.rejectOpeningFor`,
    defaultMessage: 'Reject the opening {opening}',
    description:
      'Accessible name of the Reject control on a queue row, naming which opening it refuses. {opening} is the opening title',
  },
  rejectOpeningTitle: {
    id: `${scope}.rejectOpeningTitle`,
    defaultMessage: 'Reject this opening',
    description:
      'Title of the dialog that collects why an opening is being refused',
  },
  rejectOpeningBody: {
    id: `${scope}.rejectOpeningBody`,
    defaultMessage: '{opening} stays unpublished until it is approved.',
    description:
      'Says what rejecting does, naming the opening. An opening held for approval is not live to applicants. {opening} is the opening title',
  },
  rejectOpeningReasonLabel: {
    id: `${scope}.rejectOpeningReasonLabel`,
    defaultMessage: 'Add rejection reason',
    description:
      'Label of the field holding why the opening is being rejected. Required',
  },
  rejectOpeningReasonHelper: {
    id: `${scope}.rejectOpeningReasonHelper`,
    defaultMessage:
      'The person who sent this opening for approval will see your reason, and can edit it and resubmit.',
    description:
      'Helper text under the rejection reason field, saying who reads it — which is why the reason is required',
  },
  confirmRejectOpening: {
    id: `${scope}.confirmRejectOpening`,
    defaultMessage: 'Reject opening',
    description:
      'Button that records the rejection, once a reason has been given',
  },
  rejectOpeningsTitle: {
    id: `${scope}.rejectOpeningsTitle`,
    defaultMessage: 'Reject {count} openings',
    description:
      'Title of the dialog that collects why several selected openings are being refused. {count} is how many',
  },
  rejectOpeningsBody: {
    id: `${scope}.rejectOpeningsBody`,
    defaultMessage:
      'All {count} stay unpublished, and each requester sees the same reason.',
    description:
      'Says what rejecting several openings at once does, and that one reason is recorded against every one of them. {count} is how many',
  },
  confirmRejectOpenings: {
    id: `${scope}.confirmRejectOpenings`,
    defaultMessage: 'Reject {count} openings',
    description:
      'Button that records the rejection against every selected opening. {count} is how many',
  },

  // --- searching the queue (AXHE-3934) ---

  // The label is what a screen reader hears; the placeholder is the hint a
  // sighted reader gets. Both, because a placeholder is not a name — it
  // disappears the moment somebody types.
  searchLabel: {
    id: `${scope}.searchLabel`,
    defaultMessage: 'Search approvals',
    description: 'Accessible name of the queue search field',
  },
  searchPlaceholder: {
    id: `${scope}.searchPlaceholder`,
    defaultMessage: 'Opening or applicant',
    description:
      'Hint inside the search field, naming what the server matches on — the opening title, and on an offer the applicant name',
  },
  noSearchMatchesTitle: {
    id: `${scope}.noSearchMatchesTitle`,
    defaultMessage: 'No approvals match "{term}"',
    description:
      'Title of the queue when a search matched nothing, quoting what was searched for. {term} is the search text',
  },

  // --- selecting rows for a bulk decision (AXHE-3934) ---
  selectRow: {
    id: `${scope}.selectRow`,
    defaultMessage: 'Select the opening {opening}',
    description:
      "Accessible name of a row's checkbox, naming which opening it selects so the name is unique in a list. {opening} is the opening title",
  },
  selectAll: {
    id: `${scope}.selectAll`,
    defaultMessage: 'Select every opening on this page you can decide',
    description:
      "Accessible name of the header checkbox. It covers only the rows a bulk decision can act on — offers need reading, and a row that is not this user's turn would be refused",
  },
  columnSelect: {
    id: `${scope}.columnSelect`,
    defaultMessage: 'Select',
    description:
      'Accessible name of the checkbox column. No visible heading — the checkbox in it is self-describing',
  },
  bulkSelected: {
    id: `${scope}.bulkSelected`,
    defaultMessage:
      '{count, plural, one {# opening selected} other {# openings selected}}',
    description:
      'Says how many openings a bulk decision will act on, above the table',
  },
  bulkClear: {
    id: `${scope}.bulkClear`,
    defaultMessage: 'Clear selection',
    description: 'Unticks every selected row without deciding anything',
  },
  // Counts rather than names: the table is re-requested, so which rows moved is
  // read there. A sentence listing twelve openings is a wall nobody reads.
  bulkAllApproved: {
    id: `${scope}.bulkAllApproved`,
    defaultMessage:
      '{count, plural, one {# opening approved} other {# openings approved}}.',
    description:
      'Confirms a bulk approval where every one landed. {count} is how many',
  },
  bulkAllRejected: {
    id: `${scope}.bulkAllRejected`,
    defaultMessage:
      '{count, plural, one {# opening rejected} other {# openings rejected}}.',
    description:
      'Confirms a bulk rejection where every one landed. {count} is how many',
  },
  bulkPartial: {
    id: `${scope}.bulkPartial`,
    defaultMessage:
      '{succeeded} of {requested} decided. The rest are unchanged — a colleague may have decided them first.',
    description:
      'Says a bulk decision partly landed, and why the rest did not. The rows that did not move are still in the table',
  },
  loadingApprovals: {
    id: `${scope}.loadingApprovals`,
    defaultMessage: 'Loading approvals',
    description: 'Accessible name of the spinner while the queue is loading',
  },
  loadError: {
    id: `${scope}.loadError`,
    defaultMessage: "We couldn't load your approvals. Try again.",
    description:
      'Message shown above the queue when the request for it failed, alongside a retry control',
  },
  retry: {
    id: `${scope}.retry`,
    defaultMessage: 'Try again',
    description: 'Control that re-requests the queue after a failure',
  },
  emptyTitle: {
    id: `${scope}.emptyTitle`,
    defaultMessage: 'No approvals waiting on you',
    description:
      'Title of the empty state when the signed-in user has nothing to sign off',
  },
  emptyBody: {
    id: `${scope}.emptyBody`,
    defaultMessage: 'When something needs your sign-off, it appears here.',
    description:
      'Body of the empty state, saying what will fill the list. Deliberately not "an offer" — the queue holds openings too',
  },
  // --- the toolbar (AXHE-3934) ---

  // A toggle, so the label names what it filters TO rather than what it is. On
  // by default: the queue's job is "what needs me" (Design Requirements §5).
  myApprovals: {
    id: `${scope}.myApprovals`,
    defaultMessage: 'My approvals',
    description:
      'Filter limiting the queue to items where this user is an approver and it is their turn to act',
  },
  filterType: {
    id: `${scope}.filterType`,
    defaultMessage: 'Type',
    description:
      'Filter choosing whether the queue lists offers, openings, or both',
  },
  filterAllTypes: {
    id: `${scope}.filterAllTypes`,
    defaultMessage: 'All types',
    description: 'Type filter option listing both offers and openings',
  },
  filterAllStatuses: {
    id: `${scope}.filterAllStatuses`,
    defaultMessage: 'All statuses',
    description: 'Status filter option listing every outcome',
  },
  statusPending: {
    id: `${scope}.statusPending`,
    defaultMessage: 'Pending',
    description: 'An approval still waiting on someone',
  },
  statusRejected: {
    id: `${scope}.statusRejected`,
    defaultMessage: 'Rejected',
    description: 'An approval that was refused',
  },
  statusCancelled: {
    id: `${scope}.statusCancelled`,
    defaultMessage: 'Cancelled',
    description:
      'An approval ended without a decision, because the rule behind it was deleted or the item was withdrawn',
  },
  // A filtered list whose filter is invisible is partial data presented as
  // complete, so the empty state names which case it is.
  noMatchesTitle: {
    id: `${scope}.noMatchesTitle`,
    defaultMessage: 'No approvals match these filters',
    description:
      'Title of the queue when the type or status filter matched nothing',
  },
  noMatchesBody: {
    id: `${scope}.noMatchesBody`,
    defaultMessage: 'Widen the type or status filter to see more.',
    description:
      'Explains, under the no-matching-filters title, what to change',
  },
  clearFilters: {
    id: `${scope}.clearFilters`,
    defaultMessage: 'Clear filters',
    description:
      'Button restoring the queue to every type and status, so the filtered-empty state has a way out',
  },

  // --- the review panel ---
  panelTitlePending: {
    id: `${scope}.panelTitlePending`,
    defaultMessage: 'Offer pending approval',
    description: 'Title of the review panel while the offer is undecided',
  },
  panelTitleApproved: {
    id: `${scope}.panelTitleApproved`,
    defaultMessage: 'Offer approved',
    description:
      'Title of the review panel once every required level has signed off',
  },
  panelTitleRejected: {
    id: `${scope}.panelTitleRejected`,
    defaultMessage: 'Offer rejected',
    description: 'Title of the review panel once an approver has rejected it',
  },
  panelTitleCancelled: {
    id: `${scope}.panelTitleCancelled`,
    defaultMessage: 'Approval withdrawn',
    description:
      'Title of the review panel when the approval ended without a decision. Two causes reach it and the wording covers both: the rule that required it was deleted, or the offer was edited while it was still under approval (AXHE-4759), which cancels the request it was raised against',
  },
  // Not one of the four request statuses: it is a mode the panel is IN, holding
  // an edited draft nobody has been asked about yet (Design Requirements §6).
  // The request behind it still reads whatever it reads.
  panelTitlePreview: {
    id: `${scope}.panelTitlePreview`,
    defaultMessage: 'Offer preview',
    description:
      'Title of the review panel while it is showing an edited draft that has not been submitted for approval yet',
  },
  panelSubtitle: {
    id: `${scope}.panelSubtitle`,
    defaultMessage: '{applicant} — {position}',
    description:
      'Subtitle of the review panel, pairing who the offer is for with what it is for',
  },
  loadingRequest: {
    id: `${scope}.loadingRequest`,
    defaultMessage: 'Loading the offer',
    description:
      'Accessible name of the spinner while the review panel is loading',
  },
  requestLoadError: {
    id: `${scope}.requestLoadError`,
    defaultMessage:
      "We couldn't load this offer. Close the panel and try again.",
    description:
      'Message shown inside the review panel when the request for it failed',
  },

  // --- the approver rail ---
  approversHeading: {
    id: `${scope}.approversHeading`,
    defaultMessage: 'Offer approvers',
    description: 'Heading of the rail listing who has to sign the offer off',
  },

  // --- document actions ---
  documentActionsHeading: {
    id: `${scope}.documentActionsHeading`,
    defaultMessage: 'Document actions',
    description: 'Heading of the rail section holding the download control',
  },
  downloadOffer: {
    id: `${scope}.downloadOffer`,
    defaultMessage: 'Download offer',
    description: 'Control that downloads the offer document being reviewed',
  },
  documentPreviewTitle: {
    id: `${scope}.documentPreviewTitle`,
    defaultMessage: 'Offer letter',
    description:
      'Accessible name of the frame the offer document is rendered in',
  },
  documentUnavailable: {
    id: `${scope}.documentUnavailable`,
    defaultMessage:
      'This offer has no document yet. Ask the person who sent it to regenerate it.',
    description:
      'Shown in place of the offer document when the letter has not been rendered, saying who can fix it',
  },

  // --- the decision footer ---
  approveOffer: {
    id: `${scope}.approveOffer`,
    defaultMessage: 'Approve offer',
    description: "Control that records the signed-in approver's sign-off",
  },
  rejectOffer: {
    id: `${scope}.rejectOffer`,
    defaultMessage: 'Reject offer',
    description:
      'Control that opens the reason field, and then records the rejection',
  },
  // Wording from the ratified Design Requirements §6. The button below keeps
  // "Reject offer" rather than the doc's "Confirm rejection": copywriting-voice
  // requires verb + noun on a destructive control and rules out confirm-style
  // labels outright.
  rejectReasonLabel: {
    id: `${scope}.rejectReasonLabel`,
    defaultMessage: 'Add rejection reason',
    description: 'Label of the field holding why the offer is being rejected',
  },
  rejectReasonHelper: {
    id: `${scope}.rejectReasonHelper`,
    defaultMessage:
      'Leave a note explaining why you are rejecting this offer. The requester will see this reason.',
    description:
      'Helper text under the rejection reason field, saying who reads it — the reason is required for that reason',
  },
  decisionRecordedApproved: {
    id: `${scope}.decisionRecordedApproved`,
    defaultMessage: 'Your approval was recorded.',
    description:
      'Confirms the sign-off landed, announced in place of the buttons that recorded it',
  },
  decisionRecordedRejected: {
    id: `${scope}.decisionRecordedRejected`,
    defaultMessage: 'Your rejection was recorded.',
    description: 'Confirms the rejection landed',
  },
  alreadyResponded: {
    id: `${scope}.alreadyResponded`,
    defaultMessage: 'You have already responded to this offer.',
    description:
      'Explains why the approve and reject controls are absent for an approver who has decided',
  },
  waitingOnOthers: {
    id: `${scope}.waitingOnOthers`,
    defaultMessage: 'This offer is waiting on another level of sign-off.',
    description:
      'Explains why the approve and reject controls are absent: the levels this person is on have not been reached yet',
  },
  observerOnly: {
    id: `${scope}.observerOnly`,
    defaultMessage:
      'You sent this offer for approval. Only its approvers can decide it.',
    description:
      'Explains why the approve and reject controls are absent for the person who submitted the offer rather than an approver on it — they can read it but not decide it',
  },

  // --- previous submissions (Design Requirements §6) ---
  previousSubmissions: {
    id: `${scope}.previousSubmissions`,
    defaultMessage:
      '{count, plural, one {# previous submission} other {# previous submissions}}',
    description:
      'Control that expands the history of earlier attempts at this offer, counting them so an approver can see there were any without expanding',
  },
  submissionNumber: {
    id: `${scope}.submissionNumber`,
    defaultMessage: 'Submission {number}',
    description: 'Names one earlier attempt at this offer by its order',
  },
  submissionApproved: {
    id: `${scope}.submissionApproved`,
    defaultMessage: 'Approved',
    description: 'Outcome of an earlier submission that cleared every level',
  },
  submissionRejected: {
    id: `${scope}.submissionRejected`,
    defaultMessage: 'Rejected',
    description: 'Outcome of an earlier submission an approver refused',
  },
  submissionCancelled: {
    id: `${scope}.submissionCancelled`,
    defaultMessage: 'Withdrawn',
    description:
      'Outcome of an earlier submission that was cancelled rather than decided, because the rule requiring it is gone',
  },

  requestSettled: {
    id: `${scope}.requestSettled`,
    defaultMessage: 'This offer has already been decided. No action is needed.',
    description:
      'Shown in place of the decision controls when the request has been decided — approved or rejected. Distinct from "waiting on others", which would claim the offer is still moving, and from the withdrawn case below, where nobody decided anything',
  },
  // NOT `requestSettled`. A withdrawn request was not decided and it is not
  // finished business: the offer is back at draft and has to be submitted again
  // (AXHE-4759). "Already been decided. No action is needed." is false on both
  // counts, and a reader who trusts it walks away from an offer nobody is
  // holding.
  requestWithdrawn: {
    id: `${scope}.requestWithdrawn`,
    defaultMessage:
      'This offer is no longer waiting on approval. It has to be submitted again before it can go to the applicant.',
    description:
      'Shown in place of the decision controls when the approval ended without a decision, saying what still has to happen — the offer is back with the requester rather than finished',
  },
  decisionFailed: {
    id: `${scope}.decisionFailed`,
    defaultMessage: "We couldn't record your decision. Try again.",
    description:
      'Fallback shown when the decision request failed and the server sent no message of its own',
  },

  // --- editing the offer (Design Requirements §6, AXHE-4759) ---
  editOffer: {
    id: `${scope}.editOffer`,
    defaultMessage: 'Edit offer',
    description:
      'Control that replaces the letter with the offer form so an approver can change the offer before deciding it',
  },
  // The doc's own label for the same control on a REJECTED offer, and it earns
  // the difference: there is no decision left to make, so the action is to send
  // a revised offer back round rather than to change one under review.
  reviseAndResubmit: {
    id: `${scope}.reviseAndResubmit`,
    defaultMessage: 'Revise & resubmit',
    description:
      'Control that opens the offer form on an offer an approver has rejected, so it can be changed and sent for approval again',
  },
  previewNewOffer: {
    id: `${scope}.previewNewOffer`,
    defaultMessage: 'Preview new offer',
    description:
      'Control that saves the edited details, re-renders the letter, and shows it before it is sent for approval again',
  },
  submitForApproval: {
    id: `${scope}.submitForApproval`,
    defaultMessage: 'Submit for approval',
    description:
      'Control that sends the edited offer back through the approval rules, starting again at the first level',
  },
  offerApproved: {
    id: `${scope}.offerApproved`,
    defaultMessage: 'Offer approved',
    description:
      'Status shown in place of the decision controls once every required level has signed the offer off',
  },
  // The doc's ratified sentence, verbatim (Design Requirements §6). Two
  // sentences, so both take a full stop.
  editRestartsApproval: {
    id: `${scope}.editRestartsApproval`,
    defaultMessage:
      'Editing any offer details will re-start the approval process from the beginning. Approvers who have already approved will need to review and approve the offer again.',
    description:
      'Warning above the offer form, shown when the offer is still under approval or already approved, saying that changing it discards the approvals collected so far',
  },
  editIntro: {
    id: `${scope}.editIntro`,
    defaultMessage: 'Fill in the details of the offer for {applicant}.',
    description:
      'Introduces the offer form inside the review panel, naming who the offer is for',
  },
  loadingForm: {
    id: `${scope}.loadingForm`,
    defaultMessage: 'Loading the offer details',
    description:
      'Accessible name of the spinner while the offer form is loading inside the review panel',
  },
  formLoadError: {
    id: `${scope}.formLoadError`,
    defaultMessage:
      "We couldn't load this offer's details. Close the form and try again.",
    description:
      'Shown in place of the offer form when its definition failed to load',
  },
  previewFailed: {
    id: `${scope}.previewFailed`,
    defaultMessage: "We couldn't save your changes. Try again.",
    description:
      'Fallback shown when saving the edited offer failed and the server sent no message of its own',
  },
  submitFailed: {
    id: `${scope}.submitFailed`,
    defaultMessage:
      "We couldn't send this offer for approval. Try again.",
    description:
      'Fallback shown when resubmitting the edited offer failed and the server sent no message of its own',
  },
  resubmittedNoApproval: {
    id: `${scope}.resubmittedNoApproval`,
    defaultMessage:
      'The offer no longer needs approval and is on its way to the applicant.',
    description:
      'Confirms a resubmission that matched no approval rule — the offer was released to the applicant instead of going back to approvers, and the panel closed',
  },
});
